<template>
  <section class="dashboard-view">
    <div v-if="exportLoadingPeriod" class="dashboard-export-overlay">
      <div class="dashboard-export-overlay__content">
        <b-spinner variant="primary" class="mb-3" />
        <div class="dashboard-export-overlay__text">{{ $t('views.dashboard.statistics.exportLoading') }}</div>
      </div>
    </div>

    <header class="dashboard-hero mb-4">
      <div class="dashboard-hero__top d-flex flex-column flex-md-row align-items-md-start justify-content-md-between">
        <div class="dashboard-hero__titles">
          <h1 class="dashboard-page-title">
            {{ $t(`views.${$options.name.toLowerCase()}.title`) }}
          </h1>
        </div>
        <div class="dashboard-hero__actions mt-3 mt-md-0 ml-md-3">
          <b-dropdown
            right
            variant="primary"
            class="dashboard-export-dropdown"
            :disabled="isLoading || !!exportLoadingPeriod"
          >
            <template #button-content>
              <b-icon icon="file-earmark-excel" class="mr-2" aria-hidden="true" />
              {{ $t('views.dashboard.statistics.exportLabel') }}
            </template>
            <b-dropdown-item
              v-for="opt in exportButtonOptions"
              :key="opt.value"
              @click="downloadAccessesExcel(opt.value)"
            >
              {{ opt.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <div class="dashboard-page-separator"></div>
      <p class="dashboard-hero__subtitle text-muted mb-0">
        {{ $t('views.dashboard.subtitle') }}
      </p>
      <p v-if="exportError" class="text-danger small mt-2 mb-0">{{ exportError }}</p>
    </header>

    <b-row class="dashboard-split align-items-start">
      <!-- Kurzzeit: Heute / 7 Tage / aktueller Monat -->
      <b-col cols="12" lg="6" class="dashboard-col dashboard-col--period pr-lg-3">
        <div class="dashboard-col-header mb-3">
          <div class="dashboard-col-header__title font-weight-bold mb-1">
            {{ $t('views.dashboard.statistics.columnPeriod') }}
          </div>
        </div>

        <template v-if="monthBlock && !loadError">
          <b-row class="dashboard-kpi-row mb-4">
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="bar-chart-line-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiTotalVisits') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(monthBlock, 'totalVisitsCount', 'TotalVisitsCount')) }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="person-plus-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiNewUsers') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(monthBlock, 'newUsersCount', 'NewUsersCount')) }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="person-check-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiKnownVisits') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(monthBlock, 'knownUserVisitsCount', 'KnownUserVisitsCount')) }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="chat-dots-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiSmsVerifications') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(monthBlock, 'smsVerificationsCount', 'SmsVerificationsCount')) }}</div>
                </div>
              </div>
            </b-col>
          </b-row>

          <b-card class="dashboard-card dashboard-toolbar mb-4" body-class="py-3 px-4">
            <div class="d-flex flex-column flex-md-row align-items-md-center flex-wrap">
              <div class="mb-3 mb-md-0 mr-md-4 flex-grow-1 flex-md-grow-0">
                <span class="dashboard-field-label d-block mb-2">
                  {{ $t('views.dashboard.statistics.periodLabel') }}
                </span>
                <b-button-group class="dashboard-segment-toggle d-flex flex-wrap">
                  <b-button
                    v-for="opt in periodButtonOptions"
                    :key="opt.value"
                    :variant="shortPeriod === opt.value ? 'primary' : 'outline-light'"
                    class="dashboard-segment-btn"
                    size="md"
                    :disabled="isLoading"
                    @click="setShortPeriod(opt.value)"
                  >
                    <b-icon :icon="opt.icon" class="mr-md-1" font-scale="1.05" aria-hidden="true" />
                    <span class="d-none d-sm-inline">{{ opt.text }}</span>
                    <span class="d-sm-none">{{ opt.short }}</span>
                  </b-button>
                </b-button-group>
              </div>

              <div class="dashboard-range flex-grow-1 ml-md-auto text-md-right mt-2 mt-md-0">
                <span class="dashboard-field-label d-block mb-1">{{ $t('views.dashboard.statistics.rangeHintMonthToNow') }}</span>
                <span class="dashboard-range__value text-dark">
                  {{ formatDate(monthBlock.from) }}
                  <span class="text-muted px-1">–</span>
                  {{ formatDateTime(monthBlock.to) }}
                </span>
              </div>
            </div>
          </b-card>

          <b-card class="dashboard-card dashboard-panel mb-4" header-class="dashboard-panel__header" body-class="px-0 py-0">
            <template #header>
              <h2 class="dashboard-panel__title h5 mb-0 font-weight-bold">
                <b-icon icon="geo-alt" class="mr-2 text-primary" aria-hidden="true" />
                {{ $t('views.dashboard.statistics.locationsTitle') }}
              </h2>
            </template>
            <div class="dashboard-panel__table-wrap">
              <b-table
                :items="monthBlock.visitsByLocation"
                :fields="locationFields"
                class="dashboard-table mb-0"
                thead-class="dashboard-table__head"
                striped
                hover
                show-empty
                :empty-text="$t('views.dashboard.statistics.emptyLocations')"
                responsive
                small
              />
            </div>
          </b-card>

          <b-card class="dashboard-card dashboard-panel" header-class="dashboard-panel__header" body-class="px-0 py-0">
            <template #header>
              <h2 class="dashboard-panel__title h5 mb-0 font-weight-bold">
                <b-icon icon="star" class="mr-2 text-primary" aria-hidden="true" />
                {{ $t('views.dashboard.statistics.topUsersTitle') }}
              </h2>
            </template>
            <div class="dashboard-panel__table-wrap">
              <b-table
                :items="monthBlock.topUsers"
                :fields="topUserFields"
                class="dashboard-table mb-0"
                thead-class="dashboard-table__head"
                striped
                hover
                show-empty
                :empty-text="$t('views.dashboard.statistics.emptyTopUsers')"
                responsive
                small
              />
            </div>
          </b-card>
        </template>
      </b-col>

      <!-- 12 Monate ohne laufenden Monat -->
      <b-col cols="12" lg="6" class="dashboard-col dashboard-col--overview pl-lg-3 mt-4 mt-lg-0">
        <div class="dashboard-col-header mb-3">
          <div class="dashboard-col-header__title font-weight-bold mb-1">
            {{ $t('views.dashboard.statistics.columnOverview') }}
          </div>
          <p v-if="overviewBlock" class="small text-muted mb-0">
            {{ formatDate(overviewBlock.from) }} – {{ formatDate(overviewBlock.to) }}
          </p>
        </div>

        <template v-if="overviewBlock && !loadError">
          <b-row class="dashboard-kpi-row mb-4">
            <b-col cols="12" sm="4" class="mb-3 mb-sm-0">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="person-plus-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiNewUsers') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(overviewBlock, 'newUsersCount', 'NewUsersCount')) }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="4" class="mb-3 mb-sm-0">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="person-check-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiKnownVisits') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(overviewBlock, 'knownUserVisitsCount', 'KnownUserVisitsCount')) }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="4">
              <div class="dashboard-card dashboard-kpi">
                <div class="dashboard-kpi__icon text-primary" aria-hidden="true">
                  <b-icon icon="chat-dots-fill" font-scale="1.25" />
                </div>
                <div class="dashboard-kpi__body">
                  <div class="dashboard-field-label mb-1">{{ $t('views.dashboard.statistics.kpiSmsVerifications') }}</div>
                  <div class="dashboard-kpi__value text-dark">{{ formatCount(statField(overviewBlock, 'smsVerificationsCount', 'SmsVerificationsCount')) }}</div>
                </div>
              </div>
            </b-col>
          </b-row>

          <b-card class="dashboard-card dashboard-panel dashboard-panel--trend mb-4" header-class="dashboard-panel__header" body-class="pb-4 px-4">
            <template #header>
              <div>
                <h2 class="dashboard-panel__title h5 mb-1 font-weight-bold">
                  <b-icon icon="calendar2" class="mr-2 text-primary" aria-hidden="true" />
                  {{ $t('views.dashboard.statistics.trendTitle') }}
                </h2>
                <p class="dashboard-trend__subtitle text-muted small mb-0">
                  {{ $t('views.dashboard.statistics.trendSubtitle') }}
                </p>
              </div>
            </template>
            <div class="dashboard-trend-chart-wrap dashboard-trend-chart-wrap--sidebar">
              <canvas ref="visitTrendChart" />
            </div>
          </b-card>

          <b-card class="dashboard-card dashboard-panel dashboard-panel--trend mb-4" header-class="dashboard-panel__header" body-class="pb-4 px-4">
            <template #header>
              <div>
                <h2 class="dashboard-panel__title h5 mb-1 font-weight-bold">
                  <b-icon icon="person-plus" class="mr-2 text-primary" aria-hidden="true" />
                  {{ $t('views.dashboard.statistics.newUsersTrendTitle') }}
                </h2>
                <p class="dashboard-trend__subtitle text-muted small mb-0">
                  {{ $t('views.dashboard.statistics.newUsersTrendSubtitle') }}
                </p>
              </div>
            </template>
            <div class="dashboard-trend-chart-wrap dashboard-trend-chart-wrap--sidebar">
              <canvas ref="newUsersTrendChart" />
            </div>
          </b-card>

          <b-card class="dashboard-card dashboard-panel mb-4" header-class="dashboard-panel__header" body-class="px-0 py-0">
            <template #header>
              <h2 class="dashboard-panel__title h5 mb-0 font-weight-bold">
                <b-icon icon="geo-alt" class="mr-2 text-primary" aria-hidden="true" />
                {{ $t('views.dashboard.statistics.locationsTitle') }}
              </h2>
            </template>
            <div class="dashboard-panel__table-wrap">
              <b-table
                :items="overviewBlock.visitsByLocation"
                :fields="locationFields"
                class="dashboard-table mb-0"
                thead-class="dashboard-table__head"
                striped
                hover
                show-empty
                :empty-text="$t('views.dashboard.statistics.emptyLocations')"
                responsive
                small
              />
            </div>
          </b-card>

          <b-card class="dashboard-card dashboard-panel" header-class="dashboard-panel__header" body-class="px-0 py-0">
            <template #header>
              <h2 class="dashboard-panel__title h5 mb-0 font-weight-bold">
                <b-icon icon="star" class="mr-2 text-primary" aria-hidden="true" />
                {{ $t('views.dashboard.statistics.topUsersTitle') }}
              </h2>
            </template>
            <div class="dashboard-panel__table-wrap">
              <b-table
                :items="overviewBlock.topUsers"
                :fields="topUserFields"
                class="dashboard-table mb-0"
                thead-class="dashboard-table__head"
                striped
                hover
                show-empty
                :empty-text="$t('views.dashboard.statistics.emptyTopUsers')"
                responsive
                small
              />
            </div>
          </b-card>
        </template>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import Chart from 'chart.js/auto'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import http from '@/$plugins/http/core'
import { STOREMODULE_ENDPOINTMODULE_MAP, STOREMODULE_ENDPOINT_MAP } from '@/constants'

const DASHBOARD_BRAND_GREEN = '#8cc047'
const DASHBOARD_NEW_USERS_BAR = '#4a90a4'
const DASHBOARD_RETURNING_BAR = '#c4893a'

export default {
  name: 'Dashboard',
  data () {
    return {
      /** Links: today | 7d | currentMonth (API-Query ?period=) */
      shortPeriod: 'currentMonth',
      statistics: null,
      isLoading: false,
      loadError: null,
      visitTrendChart: null,
      newUsersTrendChart: null,
      locationFields: [
        { key: 'locationName', label: '', sortable: true },
        { key: 'visitCount', label: '', sortable: true }
      ],
      topUserFields: [
        { key: 'email', label: '', sortable: true },
        { key: 'phoneMobile', label: '', sortable: true },
        { key: 'visitCount', label: '', sortable: true }
      ],
      exportLoadingPeriod: null,
      exportError: null
    }
  },
  computed: {
    periodButtonOptions () {
      const t = (k) => this.$t(k)
      return [
        { value: 'today', text: t('views.dashboard.statistics.periodToday'), short: t('views.dashboard.statistics.periodToday'), icon: 'calendar-day' },
        { value: '7d', text: t('views.dashboard.statistics.period7d'), short: '7d', icon: 'calendar-week' },
        { value: 'currentMonth', text: t('views.dashboard.statistics.periodCurrentMonth'), short: 'Monat', icon: 'calendar3' }
      ]
    },
    exportButtonOptions () {
      const t = (k) => this.$t(k)
      return [
        { value: 'today', text: t('views.dashboard.statistics.exportToday') },
        { value: 'yesterday', text: t('views.dashboard.statistics.exportYesterday') },
        { value: 'currentMonth', text: t('views.dashboard.statistics.exportCurrentMonth') },
        { value: 'currentYear', text: t('views.dashboard.statistics.exportCurrentYear') }
      ]
    },
    monthBlock () {
      if (!this.statistics) return null
      return this.statistics.currentMonth || this.statistics.CurrentMonth || null
    },
    overviewBlock () {
      if (!this.statistics) return null
      return this.statistics.overviewThirteenMonths || this.statistics.OverviewThirteenMonths || null
    }
  },
  watch: {
    '$i18n.locale' () {
      this.applyI18nLabels()
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.syncVisitTrendChart()
          this.syncNewUsersTrendChart(0)
        })
      })
    }
  },
  created () {
    this.applyI18nLabels()
    this.fetchStatistics(true)
  },
  beforeDestroy () {
    this.destroyVisitTrendChart()
    this.destroyNewUsersTrendChart()
  },
  methods: {
    /** camelCase oder PascalCase aus API (Vue-2-Template unterstützt kein ??). */
    statField (row, camelKey, pascalKey) {
      if (row == null) return undefined
      const v = row[camelKey]
      return v != null ? v : row[pascalKey]
    },
    setShortPeriod (value) {
      if (this.shortPeriod === value || this.isLoading) return
      this.shortPeriod = value
      this.fetchStatistics(false)
    },
    applyI18nLabels () {
      const t = (k) => this.$t(k)
      this.locationFields = [
        { key: 'locationName', label: t('views.dashboard.statistics.colLocationName'), sortable: true },
        {
          key: 'visitCount',
          label: t('views.dashboard.statistics.colVisitCount'),
          class: 'text-right',
          thClass: 'text-right',
          sortable: true,
          formatter: (value) => this.formatCount(value)
        }
      ]
      this.topUserFields = [
        { key: 'phoneMobile', label: t('views.dashboard.statistics.colPhone'), sortable: true },
        { key: 'email', label: t('views.dashboard.statistics.colEmail'), sortable: true },
        {
          key: 'visitCount',
          label: t('views.dashboard.statistics.colVisitCount'),
          class: 'text-right',
          thClass: 'text-right',
          sortable: true,
          formatter: (value) => this.formatCount(value)
        }
      ]
    },
    statisticsUrl (includeOverview = true) {
      const base = `/${STOREMODULE_ENDPOINTMODULE_MAP.customer}/api/${STOREMODULE_ENDPOINT_MAP.dashboardstatistics}`
      const params = new URLSearchParams({ period: this.shortPeriod, includeOverview: String(includeOverview) })
      return `${base}?${params.toString()}`
    },
    exportAccessesUrl (period) {
      const base = `/${STOREMODULE_ENDPOINTMODULE_MAP.customer}/api/${STOREMODULE_ENDPOINT_MAP.dashboardstatistics}/ExportAccesses`
      const params = new URLSearchParams({ period })
      return `${base}?${params.toString()}`
    },
    downloadAccessesExcel (period) {
      if (this.exportLoadingPeriod) return
      this.exportLoadingPeriod = period
      this.exportError = null
      http({
        method: 'get',
        url: this.exportAccessesUrl(period),
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          const url = window.URL.createObjectURL(blob)
          const contentDisposition = response.headers['content-disposition'] || ''
          const match = contentDisposition.match(/filename="([^"]+)"/)
          const filename = match && match[1] ? match[1] : `dashboard-accesses-${period}.xlsx`
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.exportError = this.$t('views.dashboard.statistics.exportError')
        })
        .finally(() => {
          this.exportLoadingPeriod = null
        })
    },
    fetchStatistics (includeOverview = true) {
      this.isLoading = true
      this.loadError = null
      http({ method: 'get', url: this.statisticsUrl(includeOverview) })
        .then((response) => {
          if (response.data && response.data.success === true && response.data.result) {
            const result = response.data.result
            if (includeOverview || !this.statistics || !this.statistics.overviewThirteenMonths) {
              this.statistics = result
            } else {
              this.statistics = {
                ...this.statistics,
                shortPeriod: result.shortPeriod,
                currentMonth: result.currentMonth
              }
            }

            if (includeOverview) {
              this.$nextTick(() => {
                this.$nextTick(() => {
                  this.syncVisitTrendChart()
                  this.syncNewUsersTrendChart(0)
                })
              })
            }
          } else {
            this.statistics = null
            this.loadError = this.$t('views.dashboard.statistics.loadError')
          }
        })
        .catch(() => {
          this.statistics = null
          this.loadError = this.$t('views.dashboard.statistics.loadError')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    destroyVisitTrendChart () {
      if (this.visitTrendChart) {
        this.visitTrendChart.destroy()
        this.visitTrendChart = null
      }
    },
    destroyNewUsersTrendChart () {
      if (this.newUsersTrendChart) {
        this.newUsersTrendChart.destroy()
        this.newUsersTrendChart = null
      }
    },
    formatTrendMonthLabel (value) {
      if (value == null || value === '') return ''
      const d = typeof value === 'string' ? parseISO(value) : new Date(value)
      if (isNaN(d.getTime())) return ''
      return format(d, 'LLL yyyy', { locale: de })
    },
    /** API kann camelCase oder PascalCase liefern; fehlende Neukunden-Serie aus Besuchs-Trend ableiten (gleiche Monate, Werte 0). */
    normalizeNewUsersTrendSeries (overview) {
      if (!overview) return []
      const raw = overview.newUsersTrend || overview.NewUsersTrend
      if (raw && raw.length) {
        return raw.map((p) => ({
          month: p.month ?? p.Month,
          newUsersCount: Number(p.newUsersCount ?? p.NewUsersCount) || 0
        }))
      }
      const visit = overview.visitTrend || overview.VisitTrend
      if (visit && visit.length) {
        return visit.map((p) => ({
          month: p.month ?? p.Month,
          newUsersCount: 0
        }))
      }
      return []
    },
    /** Monatlich: Zugriffe von Kunden, die vor dem Monat bereits angelegt waren (gleiche Basis wie KPI „bekannte Kunden“). */
    knownVisitsValuesForMonths (overview, monthTimestamps) {
      const raw = overview && (overview.knownUserVisitsTrend || overview.KnownUserVisitsTrend)
      if (!raw || !raw.length || !monthTimestamps || !monthTimestamps.length) {
        return monthTimestamps ? monthTimestamps.map(() => 0) : []
      }
      const byMonth = new Map()
      raw.forEach((p) => {
        const m = p.month ?? p.Month
        const t = m != null ? new Date(m).getTime() : NaN
        if (!isNaN(t)) {
          byMonth.set(t, Number(p.visitCount ?? p.VisitCount) || 0)
        }
      })
      return monthTimestamps.map((m) => {
        const t = m != null ? new Date(m).getTime() : NaN
        return !isNaN(t) && byMonth.has(t) ? byMonth.get(t) : 0
      })
    },
    syncVisitTrendChart () {
      this.destroyVisitTrendChart()
      const overview = this.overviewBlock
      const canvas = this.$refs.visitTrendChart
      const visitTrend = overview && (overview.visitTrend || overview.VisitTrend)
      if (!overview || !canvas || !visitTrend || visitTrend.length === 0) return
      const labels = visitTrend.map((p) => this.formatTrendMonthLabel(p.month ?? p.Month))
      const values = visitTrend.map((p) => Number(p.visitCount ?? p.VisitCount) || 0)
      const label = this.$t('views.dashboard.statistics.trendDatasetLabel')
      const formatSwissInt = (n) => this.formatCount(n)
      this.visitTrendChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label,
              data: values,
              borderColor: DASHBOARD_BRAND_GREEN,
              backgroundColor: 'rgba(140, 192, 71, 0.15)',
              fill: true,
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: DASHBOARD_BRAND_GREEN,
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: { boxWidth: 12, usePointStyle: true }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const raw = ctx.raw
                  const n = Number(raw)
                  const num = Number.isFinite(n) ? formatSwissInt(n) : raw
                  return `${ctx.dataset.label}: ${num}`
                }
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true }
            },
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
                callback: (v) => formatSwissInt(v)
              },
              grid: { color: 'rgba(0, 0, 0, 0.06)' }
            }
          }
        }
      })
    },
    syncNewUsersTrendChart (refRetry = 0) {
      this.destroyNewUsersTrendChart()
      const overview = this.overviewBlock
      const series = this.normalizeNewUsersTrendSeries(overview)
      const canvas = this.$refs.newUsersTrendChart
      if (!overview || series.length === 0) return
      if (!canvas) {
        if (refRetry < 8) {
          this.$nextTick(() => this.syncNewUsersTrendChart(refRetry + 1))
        }
        return
      }
      const labels = series.map((p) => this.formatTrendMonthLabel(p.month))
      const monthTimestamps = series.map((p) => p.month)
      const newUsersValues = series.map((p) => p.newUsersCount)
      const knownVisitsValues = this.knownVisitsValuesForMonths(overview, monthTimestamps)
      const labelNew = this.$t('views.dashboard.statistics.newUsersDatasetLabel')
      const labelReturning = this.$t('views.dashboard.statistics.returningCustomersDatasetLabel')
      const formatSwissInt = (n) => this.formatCount(n)
      this.newUsersTrendChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: labelNew,
              data: newUsersValues,
              backgroundColor: 'rgba(74, 144, 164, 0.55)',
              borderColor: DASHBOARD_NEW_USERS_BAR,
              borderWidth: 1,
              borderRadius: 4,
              maxBarThickness: 32
            },
            {
              label: labelReturning,
              data: knownVisitsValues,
              backgroundColor: 'rgba(196, 137, 58, 0.5)',
              borderColor: DASHBOARD_RETURNING_BAR,
              borderWidth: 1,
              borderRadius: 4,
              maxBarThickness: 32
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          datasets: {
            bar: {
              categoryPercentage: 0.72,
              barPercentage: 0.9
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: { boxWidth: 12, usePointStyle: true }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const raw = ctx.raw
                  const n = Number(raw)
                  const num = Number.isFinite(n) ? formatSwissInt(n) : raw
                  return `${ctx.dataset.label}: ${num}`
                }
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true }
            },
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
                callback: (v) => formatSwissInt(v)
              },
              grid: { color: 'rgba(0, 0, 0, 0.06)' }
            }
          }
        }
      })
    },
    formatDate (value) {
      if (value == null || value === '') return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date)
    },
    formatDateTime (value) {
      if (value == null || value === '') return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date)
    },
    /** Schweizer Schreibweise: Tausender mit Apostroph (z. B. 1'234) */
    formatCount (value) {
      if (value == null || value === '') return '0'
      const n = Number(value)
      if (!Number.isFinite(n)) return String(value)
      const intVal = Math.trunc(n)
      return String(intVal).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/css/config/bootstrap-variables.scss";

// An Login-Screen: helles Grau, weisse Flächen, grüne Akzentleiste; Markenfarbe
.dashboard-view {
  position: relative;
  width: 100%;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  padding: 1.5rem 0 3rem;
  background-color: $white;
  border-radius: 0;
  box-sizing: border-box;

  .dashboard-split {
    margin-left: 0;
    margin-right: 0;
    row-gap: 1rem;
  }

  @media (min-width: 992px) {
    .dashboard-split > .dashboard-col--period {
      flex: 0 0 calc(50% - 0.5rem);
      max-width: calc(50% - 0.5rem);
      margin-right: 0.5rem;
    }

    .dashboard-split > .dashboard-col--overview {
      flex: 0 0 calc(50% - 0.5rem);
      max-width: calc(50% - 0.5rem);
      margin-left: 0.5rem;
    }
  }

  .dashboard-col-header__title {
    letter-spacing: 0;
    font-size: $h5-font-size;
    line-height: 1.25;
    font-weight: $font-weight-bold;
  }

  .dashboard-col--period {
    background-color: $gray-200;
    border: 1px solid rgba($gray-800, 0.14);
    border-radius: 0;
    padding: 1rem;
  }

  .dashboard-col--overview {
    background-color: $white;
    border: 1px solid rgba($gray-800, 0.12);
    border-radius: 0;
    padding: 1rem;
  }

  .dashboard-col--period {
    .dashboard-col-header__title {
      color: darken($primary, 6%) !important;
    }
  }

  .dashboard-col--overview {
    .dashboard-col-header__title {
      color: $gray-700 !important;
    }
  }

  @media (min-width: 992px) {
    .dashboard-col--overview {
      position: sticky;
      top: 0.75rem;
    }
  }

  .dashboard-page-title {
    margin: 0;
    color: $gray-900;
  }

  .dashboard-page-separator {
    margin-top: $spacer * 0.8;
    margin-bottom: $spacer * 0.8;
    height: 5px;
    background-color: $dark;
    width: 100%;
  }

  .dashboard-hero {
    &__actions {
      flex-shrink: 0;
    }

    &__subtitle {
      max-width: 40rem;
      line-height: 1.55;
      font-size: 0.95rem;
    }
  }

  .dashboard-export-dropdown .btn {
    min-width: 220px;
    text-align: left;
    font-weight: 600;
  }

  .dashboard-export-dropdown .btn:hover:not(:disabled),
  .dashboard-export-dropdown .btn:focus:not(:disabled) {
    box-shadow: none;
  }

  .dashboard-export-overlay {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(255, 255, 255, 0.72);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dashboard-export-overlay__content {
    min-width: 280px;
    background: $white;
    border: 1px solid rgba($gray-800, 0.15);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    padding: 1rem 1.25rem;
    text-align: center;
  }

  .dashboard-export-overlay__text {
    font-size: 0.95rem;
    font-weight: 600;
    color: $gray-800;
  }

  .dashboard-field-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: $gray-600;
  }

  .dashboard-card.card {
    background: $gray-100;
    border: 1px solid rgba($gray-800, 0.12);
    border-top: 3px solid $primary;
    border-radius: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .dashboard-col--overview .dashboard-card.card {
    background: $gray-200;
    border-top-color: $gray-500;
  }

  .dashboard-col--period .dashboard-card.card {
    background: $white;
    border-top-color: $primary;
  }

  .dashboard-toolbar .dashboard-segment-toggle .btn {
    font-weight: 600;
    border-radius: 0.35rem !important;
    margin-right: 0.35rem;
    margin-bottom: 0.35rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .dashboard-segment-toggle .btn-outline-light {
    background-color: $gray-200;
    border: 1px solid $gray-400;
    color: $gray-800;
  }

  .dashboard-segment-toggle .btn-outline-light:hover:not(:disabled) {
    background-color: darken($gray-200, 5%);
    border-color: $gray-500;
    color: $gray-900;
  }

  .dashboard-segment-toggle .btn-primary {
    box-shadow: none;
  }

  .dashboard-range__value {
    font-size: 0.9rem;
    font-weight: 600;
  }

  .dashboard-alert {
    border-radius: 0;
    border: none;
  }

  .dashboard-kpi-row .dashboard-kpi {
    display: flex;
    align-items: center;
    padding: 1.35rem 1.5rem;
    min-height: 5.5rem;
  }

  .dashboard-kpi__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    background-color: rgba($primary, 0.12);
  }

  .dashboard-kpi__value {
    font-size: 1.85rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }

  .dashboard-panel .card-header {
    background: inherit;
    border-bottom: 1px solid rgba($gray-800, 0.12);
    padding: 1rem 1.25rem;
  }

  .dashboard-panel__title {
    color: $primary;
  }

  .dashboard-panel__table-wrap {
    padding: 0 0.25rem 0.75rem;
    @media (min-width: 768px) {
      padding: 0 1.25rem 1rem;
    }
  }

  .dashboard-trend__subtitle {
    line-height: 1.45;
  }

  .dashboard-trend-chart-wrap {
    position: relative;
    height: 280px;
    width: 100%;
  }

  .dashboard-trend-chart-wrap--sidebar {
    height: 300px;
    @media (min-width: 992px) {
      height: 340px;
    }
  }

  .dashboard-table thead th {
    border-top: none;
    background-color: $gray-200;
    font-size: $font-size-sm;
    text-transform: none;
    letter-spacing: 0;
    color: $gray-700 !important;
    font-weight: $font-weight-bold;
    border-bottom: 1px solid rgba($gray-800, 0.08);
  }

  .dashboard-table tbody td {
    vertical-align: middle;
    border-color: rgba($gray-800, 0.06);
    font-size: $font-size-base;
    font-weight: $font-weight-base;
  }

  .dashboard-table tbody tr:hover {
    background-color: rgba($primary, 0.06);
  }

  .dashboard-table.table-striped tbody tr:nth-of-type(odd) {
    background-color: rgba($gray-100, 0.65);
  }

  .dashboard-table.table-striped tbody tr:nth-of-type(odd):hover {
    background-color: rgba($primary, 0.06);
  }
}
</style>
